import Glide from '@glidejs/glide';

const MULTIPLE_LOGO_INSTANCES = [
  'ageoptions',
  'newyork',
  'seagoaaa',
  'sjgov',
  'suny',
  'washington',
  'wisconsin',
  'ftaaad',
];

/**
 * Default Login Page
 *
 */
const loginApp = {
  settings: {
    debug: false,
    smallBreak: 480,
    mediumBreak: 768,
    largeBreak: 1024,
  },

  userWidth: '',

  initialize: function () {
    loginApp.logs.create('loginApp.initialize: Triggered', 'info');

    loginApp.getUserWidth();
  },

  logs: {
    create: function (message, type) {
      if (loginApp.settings.debug && window.console) {
        switch (type) {
          case 'info':
            console.info(message);
            break;
          case 'warn':
            console.warn(message);
            break;
          case 'error':
            console.error(message);
            break;
          default:
            console.log(message);
            break;
        }
      }
    },
  },

  getUserWidth: function () {
    loginApp.logs.create('loginApp.getUserWidth: Triggered', 'info');

    loginApp.userWidth = document.body.offsetWidth;
  },

  isMobile: function () {
    loginApp.logs.create('loginApp.isMobile: Triggered', 'info');

    return loginApp.userWidth < loginApp.settings.mediumBreak;
  },

  isTablet: function () {
    loginApp.logs.create('loginApp.isTablet: Triggered', 'info');

    return (
      loginApp.userWidth > loginApp.settings.smallBreak &&
      loginApp.userWidth < loginApp.settings.largeBreak
    );
  },

  isDesktop: function () {
    loginApp.logs.create('loginApp.isDesktop: Triggered', 'info');

    return loginApp.userWidth >= loginApp.settings.largeBreak;
  },

  displayAgencyLogos: function (container) {
    loginApp.logs.create('loginApp.displayAgencyLogos: Triggered', 'info');

    const agencyLogos = new Glide(container, {
      type: 'slider',
      autoplay: 4000,
      hoverpause: false,
      keyboard: false,
      animationDuration: 2000,
      perView: 1,
      focusAt: 'center',
      startAt: 0,
    });

    if (loginApp.isMobile() && document.querySelectorAll('.agency-logo').length > 1) {
      agencyLogos.mount();
    }

    if (
      loginApp.isTablet() &&
      document.body.classList.contains('signup') &&
      document.querySelectorAll('.agency-logo').length > 2
    ) {
      agencyLogos.mount();
    }

    /**
     * Load slider of logos on the desktop version of the Signup Invite page for some instances only
     * since they have too many logos for the space available.
     *
     * TODO (Mariana): Refactor this in order to remove the hardcoded instances
     */
    if (loginApp.shouldMountGlideOnAgencyLogos()) {
      agencyLogos.mount();
    }
  },

  displayTestimonials: function (container) {
    loginApp.logs.create('loginApp.displayTestimonials: Triggered', 'info');

    new Glide(container, {
      type: 'carousel',
      gap: 18,
      autoplay: 6000,
      animationDuration: 2000,
    }).mount();
  },

  initializePopupButtons: function () {
    loginApp.logs.create('loginApp.initializePopupButtons: Triggered', 'info');

    const openButtons = document.querySelectorAll('.js-open-popup');
    const closeButtons = document.querySelectorAll('.js-close-popup');

    for (let i = 0; i < openButtons.length; i++) {
      openButtons[i].addEventListener(
        'click',
        function (e) {
          e.preventDefault();
          loginApp.openPopup(document.getElementById(this.dataset.id));
        },
        false
      );
    }

    for (let i = 0; i < closeButtons.length; i++) {
      closeButtons[i].addEventListener(
        'click',
        function (e) {
          e.preventDefault();
          loginApp.closePopup(document.getElementById(this.dataset.id));
        },
        false
      );
    }

    document.addEventListener(
      'click',
      function (event) {
        if (!event.target.closest('.modal') && !event.target.closest('.js-open-popup')) {
          const modals = document.getElementsByClassName('modal active');

          for (let i = 0; i < modals.length; i++) {
            modals[i].classList.remove('active');
          }
          document.body.classList.remove('overlay');
        }
      },
      false
    );
  },

  openPopup: function (container) {
    loginApp.logs.create('loginApp.openPopup: Triggered', 'info');

    window.scrollTo({ top: 0, behavior: 'smooth' });
    document.body.classList.add('overlay');
    container.classList.add('active');
  },

  closePopup: function (container) {
    loginApp.logs.create('loginApp.closePopup: Triggered', 'info');

    document.body.classList.remove('overlay');
    container.classList.remove('active');
  },

  /**
   * Lazy loads images by replacing data-src with src when they come into view
   * Uses IntersectionObserver API for modern performance
   */
  lazyLoadImages: function () {
    const lazyImages = document.querySelectorAll('.lazy');

    if (lazyImages.length === 0) return;

    loginApp.logs.create('loginApp.lazyLoadImages: Triggered', 'info');

    const imageObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const image = entry.target;
            image.src = image.dataset.src;
            image.classList.remove('lazy');
            imageObserver.unobserve(image);
          }
        });
      },
      {
        rootMargin: '0px 0px 300px 0px', // Load images 300px before they enter viewport
      }
    );

    lazyImages.forEach((image) => imageObserver.observe(image));
  },

  formAutoScroll: function (form) {
    if (form) {
      // Create array of all non-hidden inputs in the form.
      const formInputs = form.querySelectorAll('input:not([type=hidden])');

      // If is a touch device add event listeners to perform auto scroll.
      const isTouchDevice = window.matchMedia('(pointer: coarse)').matches;

      if (isTouchDevice) {
        const scrollOptions = { behavior: 'smooth', block: 'start' };

        // Focus the first form input
        formInputs[0].focus();

        form.scrollIntoView(scrollOptions);

        // Iterate over form inputs and add event listeners to initiate auto scroll when focused.
        for (let i = 0; i < formInputs.length; i++) {
          if (formInputs[i].type !== 'hidden') {
            formInputs[i].addEventListener('focus', function () {
              formInputs[i].scrollIntoView(scrollOptions);
            });
          }
        }
      } else {
        formInputs[0].focus();
      }
    }
  },

  shouldMountGlideOnAgencyLogos: function () {
    if (!loginApp.isDesktop() || !document.body.classList.contains('sign-page')) {
      return false;
    }

    return MULTIPLE_LOGO_INSTANCES.some((className) => document.body.classList.contains(className));
  },
};

document.addEventListener('DOMContentLoaded', function () {
  loginApp.initialize();
  loginApp.displayAgencyLogos(document.getElementById('js-agency-logos'));
  loginApp.initializePopupButtons();
  loginApp.lazyLoadImages();

  // Login Page
  if (document.body.classList.contains('login')) {
    loginApp.formAutoScroll(document.getElementById('login-form'));

    // Prevent double-click
    document.getElementById('login-form').addEventListener('submit', function () {
      document.getElementById('btn_login').setAttribute('disabled', true);
    });
  }

  //Check that testimonials slider exists
  if (document.getElementById('js-testimonials-slider')) {
    loginApp.displayTestimonials(document.getElementById('js-testimonials-slider'));
  }
});
